import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { message } from 'antd';
import { COMMON, ifyouApi, STORY } from 'apis/config';
import ErrorMessage from 'apis/ErrorMessage';
import fetchLocalizingInput from 'hooks/fetchLocalizingInput';
import { IDropListProps } from 'utils/types/common';

export const dlcApi = {
  read: (projectId: string | undefined) =>
    ifyouApi.post(`${STORY}/${projectId}/dlc`).then((res) => res.data),
  detail: (projectId: string | undefined, dlcId: string | undefined) =>
    ifyouApi.post(`${STORY}/${projectId}/dlc/${dlcId}`).then((res) => res.data),
  register: ({ projectId, inputData }) =>
    ifyouApi
      .post(`${STORY}/${projectId}/dlc/create`, {
        dlc_name: inputData.dlc_name,
        dlc_type: inputData.dlc_type,
      })
      .then((res) => res.data),
  update: ({ projectId, dlcId, detailData, langList }) =>
    ifyouApi
      .post(`${STORY}/${projectId}/dlc/${dlcId}/update`, {
        dlc_name: detailData.dlc_name,
        dlc_type: detailData.dlc_type,
        price: detailData.price,
        sale_price: detailData.sale_price,
        cast1: detailData.cast1,
        cast2: detailData.cast2,
        cast3: detailData.cast3,
        cast4: detailData.cast4,
        detail: langList,
      })
      .then((res) => res.data),
  episodeRead: (projectId, dlcId) =>
    ifyouApi
      .post(`${STORY}/${projectId}/dlc/${dlcId}/episode-list`)
      .then((res) => res.data),
  episodeDetail: (projectId, dlcId, episodeId, lang) =>
    ifyouApi
      .post(`${STORY}/${projectId}/dlc/${dlcId}/episode-detail`, {
        episode_id: episodeId,
        lang: lang,
      })
      .then((res) => res.data),
  episodeRegister: ({ projectId, dlcId, inputData }) =>
    ifyouApi
      .post(`${STORY}/${projectId}/dlc/${dlcId}/episode-insert`, {
        episode_type: inputData.episode_type,
        title: inputData.title,
        lang: inputData.lang,
      })
      .then((res) => res.data),
  episodeUpdate: ({ projectId, dlcId, detailData, langList }) =>
    ifyouApi
      .post(`${STORY}/${projectId}/dlc/${dlcId}/episode-update`, {
        episode_id: detailData.episode_id,
        ending_type: detailData.ending_type,
        depend_episode: detailData.depend_episode,
        speaker: detailData.speaker,
        popup_image_id: detailData.popup_image_id,
        langList: langList,
      })
      .then((res) => res.data),
  episodeSort: (projectId, dlcId, sortData) =>
    ifyouApi
      .post(`${STORY}/${projectId}/dlc/${dlcId}/episode-sort`, {
        rows: sortData,
      })
      .then((res) => res.data)
      .catch((error) => {
        if (error.response) {
          ErrorMessage(error.response.status, error.response.data);
        }
      }),
};

export const fetchDropDlcApi = {
  dlcType: () =>
    ifyouApi
      .post(`${COMMON}`, {
        func: 'standard',
        code: 'dlc_type',
      })
      .then((res) => res.data),
  bannerImg: (projectId) =>
    ifyouApi
      .post(`${COMMON}`, {
        func: 'projectImageList',
        code: 'dlc_banner',
        project_id: projectId,
      })
      .then((res) => res?.data?.map((v, i) => ({ ...v, key: i }))),
};

/// DLC 목록
export const useDlcList = (projectId: string | undefined) => {
  const { status, data, error } = useQuery(
    ['dlcListData', projectId],
    async () => await dlcApi.read(projectId),
    { enabled: !!projectId },
  );
  return { status, data, error };
};

/// DLC 디테일
export const useDlcDetail = (
  projectId: string | undefined,
  dlcId: string | undefined,
  setDetailData,
  langList,
  setLangList,
) => {
  const { status, data, error } = useQuery(
    ['dlcDetailData', projectId, dlcId],
    async () => await dlcApi.detail(projectId, dlcId),
    {
      enabled: !!dlcId,
      onSuccess: (data) => {
        setDetailData(data.master);
        if (data.detail.length > 0) {
          fetchLocalizingInput(data.detail, setLangList);
        } else {
          fetchLocalizingInput(langList, setLangList);
        }
      },
    },
  );
  return { status, data, error };
};

/// DLC 정보 등록
export const useDlcRegister = (setVisible, setInputData) => {
  const queryClient = useQueryClient();

  return useMutation(async (props: any) => await dlcApi.register(props), {
    onSuccess: (data) => {
      setVisible(false);
      setInputData({
        dlc_name: '',
        dlc_type: 'free',
      });
      queryClient.invalidateQueries(['dlcListData']);
      message.success('DLC가 등록되었습니다.', 1);
    },
    onError: (err: any) => {
      console.log(`DLC 등록 에러 : ${err}`);
      setVisible(true);
      setInputData({
        dlc_name: '',
        dlc_type: 'free',
      });
      if (err?.response) {
        ErrorMessage(err.response.status, err.response.data);
      }
    },
  });
};

/// DLC 수정
export const useDlcUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation(async (props: any) => await dlcApi.update(props), {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['dlcDetailData']);
      message.success('DLC가 수정되었습니다.', 1);
    },
    onError: (err: any) => {
      console.log(`DLC 수정 에러 : ${err}`);
      if (err?.response) {
        ErrorMessage(err.response.status, err.response.data);
      }
    },
  });
};

export const useDlcTypeDrop = () => {
  const { status, data, error } = useQuery(
    ['dlcTypeDropList'],
    async () => await fetchDropDlcApi.dlcType(),
    {
      select: (data) => {
        const newArr: IDropListProps[] = [];
        data?.map((v, i) =>
          newArr.push({
            key: i,
            value: v.code,
            text: v.code_name,
          }),
        );
        return newArr;
      },
    },
  );
  return { status, data, error };
};

export const useDlcBannerDrop = (projectId: string) => {
  return useQuery(
    ['dlcBannerImgDropList', projectId],
    async () => await fetchDropDlcApi.bannerImg(projectId),
    {
      enabled: !!projectId,
      onSuccess: (data) => data?.map((v, i) => ({ ...v, key: i })),
    },
  );
};

/// dlc-episode 목록
export const useDlcEpisodeList = (projectId, dlcId, setDlcEpisodeList) => {
  const { status, data, error } = useQuery(
    ['dlcEpisodeListData', projectId, dlcId],
    async () => await dlcApi.episodeRead(projectId, dlcId),
    {
      enabled: !!dlcId,
      onSuccess: (data) => {
        setDlcEpisodeList(data.map((v, i) => ({ ...v, key: i })));
        return data;
      },
    },
  );
  return { status, data, error };
};

/// dlc-episode 디테일
export const useDlcEpsiodeDetail = (
  projectId: string | undefined,
  dlcId: string | undefined,
  episodeId,
  lang,
  setDetailData,
  langList,
  setLangList,
) => {
  const { status, data, error } = useQuery(
    ['dlcEpisodeDetailData', projectId, dlcId],
    async () => await dlcApi.episodeDetail(projectId, dlcId, episodeId, lang),
    {
      enabled: !!episodeId,
      onSuccess: (data) => {
        if (data.detail.length > 0) {
          setDetailData({ ...data.detail[0], episode_id: episodeId });
        }
        if (data.langList.length > 0) {
          fetchLocalizingInput(data.langList, setLangList);
        } else {
          fetchLocalizingInput(langList, setLangList);
        }
      },
    },
  );
  return { status, data, error };
};

/// dlc-episode 등록
export const useDlcEpisodeRegister = (setVisible) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (props: any) => await dlcApi.episodeRegister(props),
    {
      onSuccess: (data) => {
        setVisible(false);
        queryClient.invalidateQueries(['dlcEpisodeListData']);
        message.success('DLC 에피소드가 등록되었습니다.', 1);
      },
      onError: (err: any) => {
        console.log(`DLC Episode 등록 에러 : ${err}`);
        setVisible(false);
        if (err?.response) {
          ErrorMessage(err.response.status, err.response.data);
        }
      },
    },
  );
};

/// dlc-episode 수정
export const useDlcEpisodeUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation(async (props: any) => await dlcApi.episodeUpdate(props), {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['dlcEpisodeDetailData']);
      message.success('DLC 에피소드가 수정되었습니다.', 1);
    },
    onError: (err: any) => {
      console.log(`DLC Episode 수정 에러 : ${err}`);
      if (err?.response) {
        ErrorMessage(err.response.status, err.response.data);
      }
    },
  });
};
